import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { Button, Card, CardBody, CardHeader, Col, Input, Row, UncontrolledTooltip } from 'reactstrap'
import { getQueryData, setQueryData } from '../../api/helpers'
import { Image } from '../../api/images/types'
import Loader from '../Common/Loader'
import useLocaleOptions from '../Hooks/useLocaleOptions'
import { ImageCardProps } from './types'

const ImageCard = (props: ImageCardProps) => {
    const { image, src, isLoading, handleSelectImage, handleEditorImage, handlePhotoSync, handleMoveToTop } = props

    const { t } = useTranslation()
    const localeOptions = useLocaleOptions()

    const currentLang = localStorage.getItem('i18nextLng') || 'hr'
    const defaultOption = localeOptions.find((option) => option.value === currentLang) || localeOptions[0]

    const [selectedLocale, setSelectedLocale] = useState(defaultOption)
    const [localDescription, setLocalDescription] = useState(image?.translations?.[selectedLocale?.value]?.fileDescription ?? '')

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDescription(e.target.value)
    }, [])

    const handleInputBlur = useCallback(() => {
        const locale = selectedLocale.value
        const { collection, module, parent } = image
        const images = getQueryData([collection, module, parent.toString()]) as Image[]

        const updatedImages = images?.map((img) =>
            img.id === image.id
                ? {
                      ...img,
                      translations: {
                          ...img.translations,
                          [locale]: {
                              fileDescription: localDescription,
                              isTranslationsUpdate: true,
                          },
                      },
                  }
                : img
        )

        setQueryData([collection, module, parent.toString()], updatedImages)
    }, [selectedLocale, image, localDescription])

    const handleLocaleChange = useCallback(
        (option: { value: string; label: string } | null) => {
            if (!option) return

            setSelectedLocale(option)
            setLocalDescription(image?.translations?.[option.value]?.fileDescription ?? '')
        },
        [image]
    )

    return (
        <Card className={'dmn-photo-card'} data-testid="photo-card">
            <CardHeader className="bg-dark border-0 px-2 py-1">
                <div className="float-end d-flex align-items-center">
                    <Button onClick={() => handleMoveToTop(image.id)} color="dark" className="btn-icon" data-testid="move-to-top-button">
                        <i className="mdi mdi-arrow-up fs-19" />
                    </Button>
                    <Button onClick={() => handleEditorImage(image)} color="dark" className="btn-icon" data-testid="full-size-button">
                        <i className="mdi mdi-arrow-top-right-bottom-left fs-19" />
                    </Button>
                    <div className="form-check ms-2">
                        <Input
                            onChange={() => handleSelectImage(image.id)}
                            type="checkbox"
                            className={'form-check-input'}
                            checked={image.checked || false}
                        />
                    </div>
                </div>
                <Button color="dark" className="btn-icon" style={{ cursor: 'move' }} onClick={(e) => e.preventDefault()}>
                    <i className="mdi mdi-menu fs-19" />
                </Button>
            </CardHeader>

            {isLoading ? (
                <Loader />
            ) : (
                <div className="card-img" style={{ position: 'relative', overflow: 'hidden' }}>
                    <UncontrolledTooltip target={`fileSync_${image.id}`}>
                        {image.fileSync ? t('form.common.photo.synchronized') : t('form.common.photo.markForSync')}
                    </UncontrolledTooltip>
                    <span
                        id={`fileSync_${image.id}`}
                        className={`text-success dmn-photo-sync-icon rounded-circle ${image.fileSync ? 'dmn-photo-synced' : ''}`}
                        onClick={() => handlePhotoSync(image.id)}
                        data-testid="sync-icon"
                    >
                        <i className="mdi mdi-check" />
                    </span>

                    <img className="img-fluid" src={src} alt={image?.translations?.[currentLang]?.fileDescription} />
                </div>
            )}

            <CardBody>
                <Row className="align-items-center">
                    <Col sm={7}>
                        <Input
                            type="text"
                            className="form-control"
                            value={localDescription}
                            id={image.id.toString()}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                        />
                    </Col>
                    <Col sm={5}>
                        <Select
                            onChange={handleLocaleChange}
                            options={localeOptions}
                            name={`photo_locale${image.id}`}
                            id={`photo_locale${image.id}`}
                            value={selectedLocale}
                        />
                    </Col>
                </Row>
            </CardBody>
        </Card>
    )
}

export default memo(ImageCard)
